import {
  ConfirmDialog,
  createTrip,
  deleteTrip,
  formatWhole,
  listTrips,
  Popover,
  type TripSummary,
  MoreIcon,
  setArchived,
  updateTrip,
} from '@trippath/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import { KINS } from '../../components/family/Family';
import { treeOf } from '../../lib/tripTree';
import { day, dayShort } from '../../lib/format';
import { TripDialog } from '../../components/trip/TripDialog';
import { CopyDialog } from './CopyDialog';

type State = { kind: 'loading' } | { kind: 'failed' } | { kind: 'ready'; trips: TripSummary[] };

/** A trip's own ⋯ menu on its card: edit its name, dates and budget, copy it to other dates,
 * archive or delete it — an editor only copies it. */
function TripMenu({
  trip,
  branches,
  onChanged,
}: {
  trip: TripSummary;
  branches: number;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [copying, setCopying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [failed, setFailed] = useState(false);
  const owner = trip.role === 'owner';
  return (
    <>
      <Popover
        open={open}
        onOpenChange={setOpen}
        label={t('trips.menu', { name: trip.name })}
        trigger={(props) => (
          <button
            type="button"
            className="trip-card-menu"
            aria-label={t('trips.menu', { name: trip.name })}
            {...props}
          >
            <MoreIcon />
          </button>
        )}
      >
        <div
          className="data-menu trip-menu"
          role="menu"
          aria-label={t('trips.menu', { name: trip.name })}
        >
          {/* Its name, first day and budget in a dialog, here: the list stays as it is. */}
          {owner && (
            <button
              type="button"
              role="menuitem"
              className="data-menu-item"
              onClick={() => {
                setOpen(false);
                setEditing(true);
              }}
            >
              {t('trips.edit')}
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className="data-menu-item trip-menu-two"
            onClick={() => {
              setOpen(false);
              setCopying(true);
            }}
          >
            <span>{t('trips.copy')}</span>
            <span className="quiet">{t('trips.copyHint')}</span>
          </button>
          {owner && (
            <button
              type="button"
              role="menuitem"
              className="data-menu-item trip-menu-two"
              onClick={() => {
                setOpen(false);
                setFailed(false);
                setArchived(trip.id, true).then(onChanged, () => {
                  setFailed(true);
                });
              }}
            >
              <span>{t('trips.archive')}</span>
              <span className="quiet">{t('trips.archiveHint')}</span>
            </button>
          )}
          {owner && (
            <button
              type="button"
              role="menuitem"
              className="data-menu-item trip-menu-danger"
              onClick={() => {
                setOpen(false);
                setDeleting(true);
              }}
            >
              {t('plan.deleteConfirm')}
            </button>
          )}
        </div>
      </Popover>
      {editing && (
        <TripDialog
          title={t('tripForm.editTitle')}
          submitLabel={t('tripForm.save')}
          initial={{
            name: trip.name,
            start: trip.start,
            budget: trip.budget.minor,
            currency: trip.budget.currency,
            purpose: trip.purpose,
          }}
          save={(input) => updateTrip(trip.id, input)}
          onClose={() => {
            setEditing(false);
          }}
          onSaved={() => {
            setEditing(false);
            onChanged();
          }}
        />
      )}
      {copying && (
        <CopyDialog
          trip={trip}
          branches={branches}
          onClose={() => {
            setCopying(false);
          }}
          onCopied={(plan) => {
            void navigate(`/trips/${plan.trip.id}`);
          }}
        />
      )}
      {failed && (
        <p className="form-failed trip-card-failed" role="alert">
          {t('plan.failed')}
        </p>
      )}
      <ConfirmDialog
        open={deleting}
        title={t('plan.deleteQuestion', { name: trip.name })}
        body={t('plan.deleteBody')}
        confirmLabel={t('plan.deleteConfirm')}
        onCancel={() => {
          setDeleting(false);
        }}
        onConfirm={() => {
          setDeleting(false);
          deleteTrip(trip.id).then(onChanged, () => {
            setFailed(true);
          });
        }}
      />
    </>
  );
}

/** An archived trip's own ⋯ menu: back into the list, editable again. */
function ArchivedMenu({ trip, onRestore }: { trip: TripSummary; onRestore: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      label={t('trips.menu', { name: trip.name })}
      trigger={(props) => (
        <button
          type="button"
          className="trip-card-menu archived-menu"
          aria-label={t('trips.menu', { name: trip.name })}
          {...props}
        >
          <MoreIcon />
        </button>
      )}
    >
      <div
        className="data-menu trip-menu"
        role="menu"
        aria-label={t('trips.menu', { name: trip.name })}
      >
        <button
          type="button"
          role="menuitem"
          className="data-menu-item trip-menu-two"
          onClick={() => {
            setOpen(false);
            onRestore();
          }}
        >
          <span>{t('trips.restore')}</span>
          <span className="quiet">{t('trips.restoreHint')}</span>
        </button>
      </div>
    </Popover>
  );
}

/** Every trip, and the way to start a new one. */
export function Trips() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [creating, setCreating] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [restoreFailed, setRestoreFailed] = useState(false);

  // The list as the server has it: on opening, and after a trip is changed or deleted.
  const reload = () => {
    listTrips().then(
      (trips) => {
        setState({ kind: 'ready', trips });
      },
      () => {
        setState({ kind: 'failed' });
      },
    );
  };
  useEffect(reload, []);

  // A family's trips: those whose parent is not listed stand first, their branches under them.
  const all = state.kind === 'ready' ? state.trips : [];
  // An archived trip is out of the list, in its own folded section.
  const trips = all.filter((trip) => trip.archivedAt === null);
  // In their families' order, each branch under its trip.
  const archived = treeOf(all).flatMap(({ trip, depth }) =>
    trip.archivedAt === null ? [] : [{ trip, depth, at: trip.archivedAt }],
  );
  // In the family's own order, as its tree in the planner: each trip before its branches.
  // Each with how many trips branch off it at any depth: those after it, deeper than it.
  const tree = treeOf(trips).map((entry, index, all) => {
    const end = all.findIndex((other, at) => at > index && other.depth <= entry.depth);
    return { ...entry, branches: (end < 0 ? all.length : end) - index - 1 };
  });
  const roots = tree.filter(({ depth }) => depth === 0);
  const below = (root: (typeof tree)[number]) => {
    const at = tree.indexOf(root);
    return tree.slice(at + 1, at + 1 + root.branches);
  };
  const met = (id: string) => trips.find((trip) => trip.id === id);
  // Its towns from where it parts to where it meets another; a long one by its two ends.
  const route = (towns: string[]) =>
    (towns.length > 4 ? [towns[0], towns.at(-1)] : towns).join(' → ');
  const item = ({ trip, branches }: (typeof tree)[number], kin: number) => (
    <li key={trip.id} className={`trip-item kin-${String(kin % KINS)}`}>
      <Link
        className="panel trip-card"
        to={trip.role === 'viewer' ? `/trips/${trip.id}/view` : `/trips/${trip.id}`}
      >
        {/* Its name, how many go, who joins it, what was cancelled (the Branches mockup, §1). */}
        <span className="trip-card-name">
          {trip.name}
          {trip.people > 0 && (
            <span className="kin-mark trip-card-people">
              {t('family.peopleShort', { count: trip.people })}
            </span>
          )}
          {trip.joined.map((joined) => (
            <span key={joined.city} className="trip-card-joined">
              {t('family.joined', { count: joined.people, city: joined.city })}
            </span>
          ))}
          {trip.cancelled > 0 && (
            <span className="trip-card-cancelled">
              {t('family.cancelled', { count: trip.cancelled })}
            </span>
          )}
          {trip.role !== 'owner' && (
            <span className="role-tag">
              {t('account.shared', { role: t(`account.role.${trip.role}`) })}
            </span>
          )}
        </span>
        <span className="quiet">
          {t('trips.dates', {
            from: day(trip.start, i18n.language),
            to: day(trip.end, i18n.language),
          })}
          {trip.route.length > 0 && ` · ${route(trip.route)}`}
        </span>
        <span className="trip-card-facts">
          {trip.purpose !== null && <span>{t(`purpose.${trip.purpose}`)}</span>}
          <span>{t('trips.days', { count: trip.days })}</span>
          <span>{t('trips.stops', { count: trip.stops })}</span>
          <span className="mono">
            {t('family.budgetIs', { amount: formatWhole(trip.budget, i18n.language) })}
          </span>
        </span>
        {trip.meets && (
          <span className="trip-card-meets">
            ↪ {t('family.joins')}{' '}
            <span className={`kin-${String((met(trip.meets.trip)?.place ?? 0) % KINS)}`}>
              <span className="kin-mark">{met(trip.meets.trip)?.name ?? '…'}</span>
            </span>{' '}
            {t('family.at', {
              city: trip.meets.city,
              day: trip.meets.day ? dayShort(trip.meets.day, i18n.language) : '',
            }).trim()}
            {trip.meets.way && ` · ${t('family.on', { way: trip.meets.way })}`}
          </span>
        )}
      </Link>
      {trip.role !== 'viewer' && (
        <TripMenu trip={trip} branches={branches} onChanged={reload} />
      )}
    </li>
  );

  return (
    <div className="page">
      <div className="page-head">
        <h1>{t('trips.title')}</h1>
        <button
          type="button"
          className="button button-primary"
          onClick={() => {
            setCreating(true);
          }}
        >
          {t('trips.new')}
        </button>
      </div>
      {state.kind === 'loading' && <p className="quiet">{t('trips.loading')}</p>}
      {state.kind === 'failed' && (
        <p className="form-failed" role="alert">
          {t('trips.failed')}
        </p>
      )}
      {state.kind === 'ready' && trips.length === 0 && archived.length === 0 && (
        <div className="welcome">
          <section className="welcome-hero" aria-labelledby="welcome-title">
            <p className="welcome-kicker">{t('empty.title')}</p>
            <h2 id="welcome-title">{t('empty.tagline')}</h2>
            <p>{t('empty.body')}</p>
            <button
              type="button"
              className="button button-primary"
              onClick={() => {
                setCreating(true);
              }}
            >
              {t('empty.start')}
            </button>
          </section>
          <ul className="welcome-steps">
            {(['route', 'money', 'road'] as const).map((step) => (
              <li key={step} className="panel">
                <span className={`welcome-icon welcome-${step}`} aria-hidden="true" />
                <h3>{t(`empty.${step}Title`)}</h3>
                <p className="quiet">{t(`empty.${step}Body`)}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
      {trips.length > 0 && (
        <ul className="trip-list">
          {roots.map((root) => {
            const branches = below(root);
            return branches.length === 0 ? (
              item(root, 0)
            ) : (
              // A family: the trip its people set out on, its branches under it.
              <li key={root.trip.id} className="trip-family">
                <ul className="trip-family-list">
                  {item(root, 0)}
                  <li className="trip-branches-item">
                    <ul className="trip-branches">
                      {branches.map((branch) => item(branch, branch.trip.place))}
                    </ul>
                  </li>
                </ul>
              </li>
            );
          })}
        </ul>
      )}
      {archived.length > 0 && (
        <section className="panel archived-trips" aria-labelledby="archived-trips-head">
          <h2 id="archived-trips-head" className="archived-trips-head">
            <button
              type="button"
              className="link-button"
              aria-expanded={showArchived}
              onClick={() => {
                setShowArchived(!showArchived);
              }}
            >
              {t('trips.archived', { count: archived.length })}
              <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
          </h2>
          {restoreFailed && (
            <p className="form-failed" role="alert">
              {t('trips.actionFailed')}
            </p>
          )}
          {showArchived && (
            <ul className="archived-list">
              {archived.map(({ trip, depth, at }) => (
                // The whole row opens it; in its family's colour, a branch under its trip.
                <li
                  key={trip.id}
                  className={`archived-row kin-${String((depth === 0 ? 0 : trip.place) % KINS)}`}
                  style={{ marginLeft: `${String(depth * 24)}px` }}
                >
                  <Link
                    className="archived-what"
                    to={trip.role === 'viewer' ? `/trips/${trip.id}/view` : `/trips/${trip.id}`}
                  >
                    <span className="trip-card-name">{trip.name}</span>
                    <span className="quiet">
                      {t('trips.dates', {
                        from: day(trip.start, i18n.language),
                        to: day(trip.end, i18n.language),
                      })}
                      {trip.route.length > 0 && ` · ${route(trip.route)}`}
                      {' · '}
                      {t('trips.archivedOn', {
                        day: day(at.slice(0, 10), i18n.language),
                      })}
                    </span>
                  </Link>
                  <span className="state-tag state-tag-plain">{t('trips.readOnly')}</span>
                  {trip.role === 'owner' && (
                    <ArchivedMenu
                      trip={trip}
                      onRestore={() => {
                        setRestoreFailed(false);
                        setArchived(trip.id, false).then(reload, () => {
                          setRestoreFailed(true);
                        });
                      }}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
      {creating && (
        <TripDialog
          title={t('tripForm.newTitle')}
          submitLabel={t('tripForm.create')}
          save={createTrip}
          onClose={() => {
            setCreating(false);
          }}
          onSaved={(plan) => {
            void navigate(`/trips/${plan.trip.id}`);
          }}
        />
      )}
    </div>
  );
}
